import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const coupons = [
  { code: 'ACBNEW', desc: 'New user discount on first order', savings: 'Up to 15% off service fees', status: 'Active' },
  { code: 'ACBSHIP', desc: 'Shipping discount on orders over $100', savings: '$10 shipping credit', status: 'Active' },
  { code: 'ACB2026', desc: 'Seasonal promotion for 2026', savings: 'Variable discount', status: 'Check website' },
  { code: 'ACBREPEAT', desc: 'Loyalty discount for returning customers', savings: '5-10% off service fees', status: 'Active' },
];

const faqs = [
  { q: 'How do I use an AllChinaBuy coupon code?', a: 'Enter the coupon code during the checkout process on the AllChinaBuy website. There is usually a coupon field in the payment or shipping confirmation step. The discount will be applied automatically to eligible items or service fees. Make sure to check the coupon terms and expiration date before applying.' },
  { q: 'Do AllChinaBuy coupons expire?', a: 'Yes, most coupon codes are time-limited and may expire after a certain period or be limited to a specific number of uses. Always check the expiration date and any usage restrictions before applying a coupon to your order. We update our coupon list regularly, but availability is subject to AllChinaBuy promotional terms.' },
  { q: 'Can I combine multiple coupons?', a: 'Generally, AllChinaBuy allows only one coupon code per order. You cannot stack multiple discount codes for a single purchase. Choose the coupon that provides the highest savings for your specific order value and contents. Some special promotions may have different stacking rules, which will be noted in the terms.' },
  { q: 'Where can I find new AllChinaBuy coupons?', a: 'Check this page regularly for updated coupon codes. You can also follow AllChinaBuy official channels, join community forums, and subscribe to newsletters. New coupons are often released during holiday seasons, major sales events, and platform anniversary promotions.' },
];

export default function CouponPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">AllChinaBuy Coupon</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Coupon & Discount Guide
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Find active AllChinaBuy coupons, discount codes, and promotional offers. Learn how to apply codes, maximize savings, and stay updated on the latest deals.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>How to Save Money on AllChinaBuy</h2>
            <p>
              Using coupons and discount codes is one of the easiest ways to reduce your overall spending when shopping through AllChinaBuy. Whether you are a new user placing your first order or a returning customer looking for loyalty rewards, there are often promotional codes available that can reduce service fees, shipping costs, or even item prices. This guide explains how to find, apply, and maximize AllChinaBuy coupons.
            </p>
            <p>
              Please note that coupon availability changes frequently and the codes listed on this page may expire or become inactive. We recommend verifying each code on the AllChinaBuy website before checkout. This is an independent informational guide and is not affiliated with AllChinaBuy official promotions.
            </p>

            <h2>Current Coupon Codes</h2>
            <p>
              Below is a list of commonly available coupon codes. Always verify these on the AllChinaBuy website before applying, as terms and availability may change.
            </p>
          </div>
        </div>
      </section>

      {/* Coupon Cards */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {coupons.map((c, i) => (
              <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200/60 border-l-4 border-l-primary-400">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-lg font-bold text-primary-700 bg-primary-50 px-3 py-1 rounded-lg">{c.code}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${c.status === 'Active' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>{c.status}</span>
                </div>
                <h4 className="font-semibold text-foreground-900 mb-1">{c.desc}</h4>
                <p className="text-foreground-600 text-sm">{c.savings}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-foreground-500 text-sm mt-6">
            Codes are for reference only. Verify on AllChinaBuy before use. Terms apply.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>How to Apply Coupon Codes</h2>
            <p>
              1. <strong>Browse and select products</strong> - Add items to your cart through the AllChinaBuy spreadsheet or product pages.
            </p>
            <p>
              2. <strong>Proceed to checkout</strong> - Review your cart and confirm the items, quantities, and shipping options.
            </p>
            <p>
              3. <strong>Enter coupon code</strong> - Look for the coupon or promo code field during the payment or shipping confirmation step. Enter the code exactly as shown, including any capital letters.
            </p>
            <p>
              4. <strong>Verify the discount</strong> - Check that the discount has been applied correctly to your order total before completing the payment.
            </p>
            <p>
              5. <strong>Complete purchase</strong> - If the discount looks correct, proceed with payment and enjoy your savings.
            </p>

            <h2>Maximizing Your Savings</h2>
            <p>
              Beyond coupon codes, there are several strategies to reduce your overall costs on AllChinaBuy. First, consolidate orders to save on shipping. Second, remove unnecessary packaging (like shoe boxes) to reduce volumetric weight. Third, choose economy shipping for non-urgent items. Fourth, time your purchases around major sales events such as Double 11, Black Friday, and platform anniversary promotions. Finally, follow AllChinaBuy social media and community channels for flash deals and limited-time offers.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Coupon FAQ
          </h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-background-200/60 rounded-xl bg-background-50 overflow-hidden" itemScope itemType="https://schema.org/Question">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                >
                  <span className="font-medium text-foreground-900 text-sm md:text-base" itemProp="name">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-lg text-primary-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5" itemScope itemType="https://schema.org/Answer">
                    <p className="text-foreground-600 text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Ready to Shop with Savings?
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Browse the AllChinaBuy spreadsheet, find your favorite products, and apply your coupon code at checkout for the best deal.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`/${lang}/products/`} className="btn-primary inline-flex items-center justify-center">
                View Products <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="btn-secondary inline-flex items-center justify-center">
                Buy It Now <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}